<template>
  <div class="pitch-types-section" :class="{ desktop: $isDesktop }">
    <translate v-if="$isDesktop" tag="h2" class="section-heading">
      Pitch types & availability
    </translate>
    <translate v-else tag="h2" class="section-heading"> Pitch types </translate>
    <slot name="pre-pitch-types-list" />
    <pitch-types-list
      :pitchtypes="matchingPitchtypes"
      :charge-types-updating="chargeTypesUpdating"
    >
      <template v-if="showMatchingCriteriaText" #title>
        <matching-criteria-count-text
          :count="matchingAvailablePitchtypesCount"
        />
      </template>
      <template #card="{ pitchtype }">
        <slot name="card" :pitchtype="pitchtype" />
      </template>
    </pitch-types-list>

    <pitch-types-list
      v-if="notMatchingPitchtypes.length > 0"
      :pitchtypes="notMatchingPitchtypes"
    >
      <template #title>
        <translate>Other options</translate>
      </template>
      <template #card="{ pitchtype }">
        <slot name="card" :pitchtype="pitchtype" />
      </template>
    </pitch-types-list>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { PitchType } from '../../campsite-details/types'
import {
  getAvailablePitchtypesMatchingTypes,
  getPitchtypesMatchingTypes,
  getPitchtypesNotMatchingTypes,
  getPitchtypesMatchingTypesAndCapacity,
  getPitchtypesNotMatchingTypesOrCapacity,
} from '../../campsite-details/utils'
import { PitchTypeWithChargeType, Party } from '../types'

@Component
class PitchTypesSection extends Vue {
  @Prop()
  pitchtypes: Array<PitchType | PitchTypeWithChargeType>

  @Prop()
  selectedTypes: number[]

  @Prop()
  selectedFacets: string[]

  @Prop()
  hasChargeTypes: boolean

  @Prop({ required: false })
  chargeTypesUpdating: boolean

  @Prop()
  party: Party

  get matchingPitchtypes() {
    // For undated searches (no charge types), use capacity-aware filtering
    if (!this.hasChargeTypes) {
      return getPitchtypesMatchingTypesAndCapacity(
        this.pitchtypes,
        this.selectedTypes,
        this.selectedFacets,
        this.party,
      )
    }

    // For dated searches (with charge types), use existing logic
    return getPitchtypesMatchingTypes(
      this.pitchtypes,
      this.selectedTypes,
      this.selectedFacets,
    )
  }

  get notMatchingPitchtypes() {
    // For undated searches (no charge types), use capacity-aware filtering
    if (!this.hasChargeTypes) {
      return getPitchtypesNotMatchingTypesOrCapacity(
        this.pitchtypes,
        this.selectedTypes,
        this.selectedFacets,
        this.party,
      )
    }

    // For dated searches (with charge types), use existing logic
    return getPitchtypesNotMatchingTypes(
      this.pitchtypes,
      this.selectedTypes,
      this.selectedFacets,
    )
  }

  get matchingAvailablePitchtypes() {
    return getAvailablePitchtypesMatchingTypes(
      this.pitchtypes as PitchTypeWithChargeType[],
      this.selectedTypes,
    )
  }

  get matchingAvailablePitchtypesCount() {
    if (this.hasChargeTypes) {
      return this.matchingAvailablePitchtypes.length
    }
    return this.matchingPitchtypes.length
  }

  get showMatchingCriteriaText() {
    return this.hasChargeTypes && this.matchingAvailablePitchtypesCount > 0
  }
}

export default toNative(PitchTypesSection)
</script>
<style scoped>
.section-heading {
  color: var(--colour-dark-blue);
  font-size: 1.6em;
  font-weight: 400;
  line-height: 1.15;
  margin: 0;
  padding: 0.7em 0 0 0.5em;
}

/* desktop & tablet */
.pitch-types-section.desktop {
  margin: auto;
  max-width: var(--page-width-max);
  padding: 0.7em var(--page-width-padding) 3em;
}

.desktop .section-heading {
  padding: 0.7em 0;
}

.pitch-types-section.desktop :deep(.horizontal-search-bar) {
  background: var(--colour-page-background);
  border-radius: 1em;
  padding: 1em;
  margin: 0 0 1em 0;
  z-index: 1;
}

.pitch-types-section.desktop
  :deep(.horizontal-search-bar .check-availability-btn) {
  margin: 0;
  order: 5;
  background: var(--colour-light-green);
  color: #fff;
  font-weight: bold;
  border-radius: 6em;
  box-shadow: none;
  min-height: 3.1em;
  display: flex;
  align-items: center;
  padding: 0.8em 1em;
  border: none;
}

.pitch-types-section.desktop
  :deep(.horizontal-search-bar .check-availability-btn img) {
  filter: invert(1);
  margin-right: 0.6em;
}

.pitch-types-section.desktop
  :deep(.horizontal-search-bar .check-availability-btn:hover),
.pitch-types-section.desktop
  :deep(.horizontal-search-bar .check-availability-btn:focus-visible) {
  background: var(--colour-dark-green);
  outline: none;
}

.pitch-types-section.desktop :deep(.horizontal-search-bar .search-submit-btn) {
  display: none;
}

.pitch-types-section.desktop :deep(.no-dates .summary) {
  border: 3px solid var(--colour-orange);
}

.pitch-types-section.desktop :deep(.horizontal-search-bar .modal) {
  top: -2em;
}

@media (max-height: 780px) {
  .pitch-types-section.desktop :deep(.horizontal-search-bar .modal) {
    max-width: 27em;
    top: -3em;
  }
}
</style>
